import { TrendingUp, TrendingDown, DollarSign, CheckCircle, Circle } from 'lucide-react';
import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';

// Funzioni helper per determinare gli effetti speciali delle transazioni
const isDiabolicTransaction = (item, type) => {
  return type === 'expense' && parseFloat(item.amount) === 666;
};

const isAngelicTransaction = (item, type) => {
  return type === 'income' && parseFloat(item.amount) === 888;
};

const isTimeTravelTransaction = (item) => {
  const transactionDate = new Date(item.date);
  return transactionDate.getFullYear() === 1999 && 
         transactionDate.getMonth() === 11 && 
         transactionDate.getDate() === 31;
};

const isQuadrifoglioFortunatoTransaction = (item) => {
  return parseFloat(item.amount) === 777;
};

const isNataleMagicoTransaction = (item) => {
  const transactionDate = new Date(item.date);
  return transactionDate.getMonth() === 11 && 
         transactionDate.getDate() === 25;
};

const isCompleannoSpecialeTransaction = (item) => {
  const transactionDate = new Date(item.date);
  return transactionDate.getMonth() === 5 && 
         transactionDate.getDate() === 5;
};

const getTransactionEffect = (item, type) => {
  if (isTimeTravelTransaction(item)) {
    return 'animate-glitch';
  }
  if (isNataleMagicoTransaction(item)) {
    return 'animate-natale-magico';
  }
  if (isCompleannoSpecialeTransaction(item)) {
    return 'animate-compleanno-speciale';
  }
  if (isQuadrifoglioFortunatoTransaction(item, type)) {
    return 'animate-quadrifoglio-fortunato';
  }
  if (isDiabolicTransaction(item, type)) {
    return 'animate-flame';
  }
  if (isAngelicTransaction(item, type)) {
    return 'animate-angelic';
  }
  return '';
};

// Componente separato per gestire il swipe
const ExpenseItem = ({
  item,
  onDelete,
  onEdit,
  onShowDetail,
  getItemType,
  getCategoryIcon,
  getTransactionEffect,
  isQuadrifoglioFortunatoTransaction,
  isCompleannoSpecialeTransaction,
  isNataleMagicoTransaction,
  selectionMode = false,
  isSelected = false,
  onLongPress,
  onToggleSelection
}) => {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const threshold = 60; // px
  const longPressDuration = 450;
  const moveCancelThreshold = 10;
  const itemType = getItemType(item);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectionMode) {
      setSwipeX(0);
      setSwiping(false);
    }
  }, [selectionMode]);

  const triggerLongPress = () => {
    if (typeof onLongPress === 'function') {
      longPressTriggeredRef.current = true;
      onLongPress(item);
    }
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    startXRef.current = touch.clientX;
    startYRef.current = touch.clientY;
    longPressTriggeredRef.current = false;
    if (!selectionMode) {
      setSwiping(true);
    } else {
      setSwiping(false);
    }
    if (!selectionMode && typeof onLongPress === 'function') {
      clearLongPressTimer();
      longPressTimerRef.current = setTimeout(triggerLongPress, longPressDuration);
    }
  };
  const handleTouchMove = (e) => {
    if (startXRef.current === null || startYRef.current === null) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - startXRef.current;
    const deltaY = touch.clientY - startYRef.current;

    if (Math.abs(deltaX) > moveCancelThreshold || Math.abs(deltaY) > moveCancelThreshold) {
      clearLongPressTimer();
    }

    if (!selectionMode && swiping) {
      setSwipeX(deltaX);
    }
  };
  const handleTouchEnd = () => {
    clearLongPressTimer();
    setSwiping(false);
    startXRef.current = null;
    startYRef.current = null;

    if (selectionMode || longPressTriggeredRef.current) {
      setSwipeX(0);
      return;
    }

    if (swipeX < -threshold) {
      onDelete(item);
    } else if (swipeX > threshold) {
      onEdit(item);
    }
    setSwipeX(0);
  };

  // Mouse events (desktop)
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    longPressTriggeredRef.current = false;
    if (!selectionMode) {
      setSwiping(true);
    } else {
      setSwiping(false);
    }
    if (!selectionMode && typeof onLongPress === 'function') {
      clearLongPressTimer();
      longPressTimerRef.current = setTimeout(triggerLongPress, longPressDuration);
    }
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };
  const handleMouseMove = (e) => {
    if (startXRef.current === null || startYRef.current === null) return;
    const deltaX = e.clientX - startXRef.current;
    const deltaY = e.clientY - startYRef.current;

    if (Math.abs(deltaX) > moveCancelThreshold || Math.abs(deltaY) > moveCancelThreshold) {
      clearLongPressTimer();
    }

    if (!selectionMode && swiping) {
      setSwipeX(deltaX);
    }
  };
  const handleMouseUp = () => {
    clearLongPressTimer();
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    setSwiping(false);
    startXRef.current = null;
    startYRef.current = null;

    if (selectionMode || longPressTriggeredRef.current) {
      setSwipeX(0);
      return;
    }

    if (swipeX < -threshold) {
      onDelete(item);
    } else if (swipeX > threshold) {
      onEdit(item);
    }
    setSwipeX(0);
  };

  const handleClick = (event) => {
    if (longPressTriggeredRef.current) {
      event.preventDefault();
      event.stopPropagation();
      longPressTriggeredRef.current = false;
      return;
    }
    if (selectionMode) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof onToggleSelection === 'function') {
        onToggleSelection(item);
      }
      return;
    }
    if (typeof onShowDetail === 'function') {
      onShowDetail(item, itemType);
    }
  };

  const hoverClass = selectionMode
    ? 'hover:bg-blue-100/60 dark:hover:bg-blue-900/30'
    : 'hover:bg-gray-100 dark:hover:bg-gray-800';
  const selectionClass = selectionMode
    ? (isSelected
      ? 'bg-blue-100/80 dark:bg-blue-900/40 ring-2 ring-blue-500/60'
      : 'bg-white/70 dark:bg-gray-800/60 border border-blue-200/40 dark:border-blue-800/40')
    : '';

  return (
    <div
      className={`expense-item p-3 cursor-pointer transition-colors relative ${hoverClass} ${selectionClass} ${getTransactionEffect(item, itemType)}`}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      style={{ 
        transform: selectionMode ? 'translateX(0px)' : `translateX(${swipeX}px)`, 
        transition: swiping ? 'none' : 'transform 0.2s',
        boxShadow: selectionMode
          ? (isSelected ? '0 0 0 2px rgba(59, 130, 246, 0.35)' : '0 0 0 rgba(0,0,0,0)')
          : '2px 0 0 rgba(239, 68, 68, 0.6), -2px 0 0 rgba(59, 130, 246, 0.6)'
      }}
    >
      {selectionMode && (
        <div className="absolute top-3 right-3 z-20 pointer-events-none">
          {isSelected ? (
            <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-300" />
          ) : (
            <Circle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      )}
      {/* Overlay emoji per quadrifoglio fortunato */}
      {isQuadrifoglioFortunatoTransaction(item, itemType) && (
        <div className="emoji-overlay">
          {Array.from({ length: 15 }, (_, i) => (
            <span
              key={i}
              className="emoji-fortuna"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              {i % 2 === 0 ? '🍀' : '💰'}
            </span>
          ))}
        </div>
      )}

      {/* Overlay emoji per compleanno speciale */}
      {isCompleannoSpecialeTransaction(item) && (
        <div className="emoji-overlay">
          {Array.from({ length: 15 }, (_, i) => (
            <span
              key={i}
              className="emoji-birthday"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              {i % 2 === 0 ? '🎂' : '🎁'}
            </span>
          ))}
        </div>
      )}

      {/* Overlay emoji per natale magico */}
      {isNataleMagicoTransaction(item) && (
        <div className="emoji-overlay">
          {Array.from({ length: 15 }, (_, i) => (
            <span
              key={i}
              className="emoji-natale"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              {i % 3 === 0 ? '❄️' : i % 3 === 1 ? '🎄' : '🎁'}
            </span>
          ))}
        </div>
      )}

      <div className="relative z-10">
        <div className="flex items-start gap-3">
          {/* Category Icon */}
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-gray-200/90 dark:bg-gray-700/90 backdrop-blur-sm flex items-center justify-center text-lg mt-3">
              {getCategoryIcon(item.category, itemType)}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-0.5">
              <div className="flex-1 min-w-0 max-w-[60%]">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate text-base">
                  {item.store || 'Senza negozio'}
                </h3>
              </div>
              <div className={`flex items-end justify-end mt-3 flex-shrink-0`}>
                <span className={`px-3 py-1 rounded-xl border font-bold text-base shadow-sm 
                  ${itemType === 'expense' ? 'text-red-700 border-red-200 bg-red-50 dark:text-red-300 dark:border-red-700 dark:bg-red-900/30' : 
                    itemType === 'income' ? 'text-green-700 border-green-200 bg-green-50 dark:text-green-300 dark:border-green-700 dark:bg-green-900/30' :
                    'text-sky-700 border-sky-200 bg-sky-50 dark:text-sky-300 dark:border-sky-700 dark:bg-sky-900/30'}`}
                      style={{ textAlign: 'right' }}>
                  {(itemType === 'expense' || itemType === 'credit') ? '-' : '+'}{formatCurrency(item.amount)}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 -mt-4">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600/10 text-blue-600 border border-blue-600/20">
                  {item.category}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {format(new Date(item.date), 'HH:mm', { locale: it })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function ExpenseList({
  items,
  onDelete,
  onEdit,
  type,
  categories = [],
  onShowDetail,
  selectionMode = false,
  selectedTransactionKeys,
  onSelectionStart,
  onToggleSelection,
  dateTileOffset = 104,
  onLoadMore,
  hasMore = false
}) {
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM yyyy', { locale: it });
  };

  const getCategoryIcon = (categoryName, itemType) => {
    if (itemType === 'debt' || itemType === 'credit') {
      return '👤';
    }
    // Per il tipo mixed, usa le categorie appropriate in base al tipo di transazione
    const categoryList = type === 'mixed' 
      ? (itemType === 'expense' ? categories.expense : categories.income)
      : categories;
    const category = categoryList.find(cat => cat.name === categoryName);
    return category?.icon || '📦';
  };

  // Determina il tipo di transazione per ogni item
  const getItemType = (item) => {
    if (item._type === 'debt' || item._type === 'credit') {
      return item._type;
    }
    if (type === 'mixed') {
      return item._type || 'expense';
    }
    return type;
  };

  const getNumericAmount = (value) => {
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }
    return Math.abs(numericValue);
  };
  
  const formatRoundedAmount = (value) => {
    const roundedValue = Math.round(getNumericAmount(value));
    return Number.isFinite(roundedValue) ? roundedValue.toLocaleString('it-IT') : '0';
  };

  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (!hasMore || typeof onLoadMore !== 'function') {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      onLoadMore();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          onLoadMore();
        }
      },
      {
        rootMargin: '160px 0px'
      }
    );

    const node = loadMoreRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
      observer.disconnect();
    };
  }, [hasMore, onLoadMore]);

  // Raggruppa le transazioni per data
  const getDisplayDateKey = (value) => {
    if (!value) return null;

    const baseDate = value instanceof Date ? new Date(value.getTime()) : new Date(value);

    if (Number.isNaN(baseDate.getTime())) {
      if (typeof value === 'string') {
        return value.includes('T') ? value.split('T')[0] : value;
      }
      return null;
    }

    if (baseDate.getHours() < 3) {
      baseDate.setDate(baseDate.getDate() - 1);
    }

    const year = baseDate.getFullYear();
    const month = String(baseDate.getMonth() + 1).padStart(2, '0');
    const day = String(baseDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const groupedItems = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    const groups = [];
    let currentGroup = null;

    for (const item of items) {
      const displayDate = getDisplayDateKey(item.date) || 'unknown-date';

      if (!currentGroup || currentGroup.dateKey !== displayDate) {
        currentGroup = {
          dateKey: displayDate,
          items: [item]
        };
        groups.push(currentGroup);
      } else {
        currentGroup.items.push(item);
      }
    }

    return groups;
  }, [items]);

  const formatDateHeader = (dateString) => {
    const todayKey = getDisplayDateKey(new Date());
    const yesterdayKey = getDisplayDateKey(new Date(Date.now() - 24 * 60 * 60 * 1000));

    if (dateString === todayKey) {
      return 'Oggi';
    } else if (dateString === yesterdayKey) {
      return 'Ieri';
    }

    if (dateString === 'unknown-date' || !dateString) {
      return 'Data non disponibile';
    }

    return format(new Date(dateString), 'EEE dd MMMM yyyy', { locale: it });
  };

  if (groupedItems.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mb-6">
          <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
            {type === 'mixed' ? (
              <DollarSign className="w-10 h-10 text-gray-500 dark:text-gray-400" />
            ) : type === 'expense' ? (
              <TrendingDown className="w-10 h-10 text-gray-500 dark:text-gray-400" />
            ) : (
              <TrendingUp className="w-10 h-10 text-gray-500 dark:text-gray-400" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-2">
            {type === 'mixed' ? 'Nessuna transazione registrata' : 
             `Nessuna ${type === 'expense' ? 'spesa' : 'entrata'} registrata`}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {type === 'mixed' ? 'Aggiungi la tua prima transazione per iniziare' :
             `Aggiungi la tua prima ${type === 'expense' ? 'spesa' : 'entrata'} per iniziare`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedItems.map(({ dateKey, items: dateItems }) => {
        const dailyTotals = dateItems.reduce(
          (totals, transaction) => {
            const transactionType = getItemType(transaction);
            const amount = getNumericAmount(transaction.amount);

            if (transactionType === 'income') {
              totals.income += amount;
            } else if (transactionType === 'expense') {
              totals.expense += amount;
            }
            return totals;
          },
          { income: 0, expense: 0 }
        );

        return (
          <div key={dateKey} className="space-y-3">
            {/* Header della data */}
            <div
              className="sticky z-10 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-3 shadow-lg lg:max-w-none"
              style={{ top: Math.max(dateTileOffset, 0) }}
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  {formatDateHeader(dateKey)}
                </h3>
                <div className="flex items-center gap-2 text-right text-[11px] font-semibold leading-tight">
                  <span className="px-2 py-0.5 rounded-lg border shadow-sm text-green-700 bg-green-50 border-green-200 dark:text-green-300 dark:bg-green-900/30 dark:border-green-700">
                    {formatRoundedAmount(dailyTotals.income)}
                  </span>
                  <span className="px-2 py-0.5 rounded-lg border shadow-sm text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-700">
                    {formatRoundedAmount(dailyTotals.expense)}
                  </span>
                </div>
              </div>
            </div>
          
            {/* Transazioni del giorno */}
            <div className="space-y-3">
              {dateItems.map((item) => {
                const itemTypeForSelection = getItemType(item);
                const selectionKey = `${itemTypeForSelection}:${item.id}`;
                const isItemSelected = selectedTransactionKeys?.has
                  ? selectedTransactionKeys.has(selectionKey)
                  : false;
                return (
                  <ExpenseItem
                    key={item.id}
                    item={item}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onShowDetail={onShowDetail}
                    getItemType={getItemType}
                    getCategoryIcon={getCategoryIcon}
                    getTransactionEffect={getTransactionEffect}
                    isQuadrifoglioFortunatoTransaction={isQuadrifoglioFortunatoTransaction}
                    isCompleannoSpecialeTransaction={isCompleannoSpecialeTransaction}
                    isNataleMagicoTransaction={isNataleMagicoTransaction}
                    selectionMode={selectionMode}
                    isSelected={isItemSelected}
                    onLongPress={onSelectionStart ? () => onSelectionStart(item) : undefined}
                    onToggleSelection={onToggleSelection ? () => onToggleSelection(item) : undefined}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {hasMore && (
        <div
          ref={loadMoreRef}
          className="py-6 text-center text-sm text-gray-500 dark:text-gray-400"
        >
          Carico altre transazioni...
        </div>
      )}
    </div>
  );
}

const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.type !== nextProps.type) {
    return false;
  }
  if (prevProps.selectionMode !== nextProps.selectionMode) {
    return false;
  }
  if (prevProps.items !== nextProps.items) {
    return false;
  }
  if (prevProps.selectedTransactionKeys !== nextProps.selectedTransactionKeys) {
    return false;
  }
  if (prevProps.categories !== nextProps.categories) {
    return false;
  }
  if (prevProps.dateTileOffset !== nextProps.dateTileOffset) {
    return false;
  }
  if (prevProps.hasMore !== nextProps.hasMore) {
    return false;
  }
  if (prevProps.onLoadMore !== nextProps.onLoadMore) {
    return false;
  }
  return true;
};

export default memo(ExpenseList, arePropsEqual); 
